import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { MATCHING_DECKS, MATCH_TYPES } from '../data/matching';
import { saveMatchingVariationSelections, getMatchingVariationSelections } from '../services/firestoreService';
import { useTranslation } from '../i18n/useTranslation';
import '../styles/session.css';
import '../styles/matching.css';

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function mediaFor(trial) {
  return trial.prompt.media?.length ? trial.prompt.media : [trial.prompt.imageUrl].filter(Boolean);
}

// Sound rounds skip the photo-variation logic entirely — the prompt is an
// audio clip (no "different photo of the same thing" concept), so the
// correct choice is simply whichever picture the admin marked correct.
function buildSoundRound(pool) {
  const trial = pickOne(pool);
  return {
    trialId: trial.id,
    prompt: { label: trial.prompt.label, audioUrl: trial.prompt.audioUrl },
    choices: shuffle(trial.choices),
  };
}

// Assembles one round: picks a random target from the enabled pool, a random
// enabled photo of it for the sample, and (when more than one photo is
// enabled) a *different* enabled photo for the correct choice — so identical
// vs. generalization matching falls out of how many photos are enabled,
// rather than being fixed at content-creation time.
function buildRound(pool, enabledMedia, matchType) {
  if (matchType === MATCH_TYPES.SOUND) return buildSoundRound(pool);

  const trial = pickOne(pool);
  const allMedia = mediaFor(trial);
  const enabled = [...(enabledMedia[trial.id] || new Set(allMedia))].filter(Boolean);
  const mediaPool = enabled.length ? enabled : allMedia;

  const promptImg = pickOne(mediaPool);
  const otherMedia = mediaPool.filter(m => m !== promptImg);
  const correctImg = otherMedia.length ? pickOne(otherMedia) : promptImg;

  const distractors = trial.choices.filter(c => !c.isCorrect);
  const choices = shuffle([
    { id: `correct-${trial.id}`, label: trial.prompt.label, imageUrl: correctImg, isCorrect: true },
    ...distractors,
  ]);

  return { trialId: trial.id, prompt: { label: trial.prompt.label, imageUrl: promptImg }, choices };
}

// ── Small feedback overlays (mirrors the deck session's celebration/flash) ──

const CelebrationOverlay = ({ visible }) => {
  if (!visible) return null;
  return (
    <div className="trial-celebration-overlay">
      <div className="trial-celebration-ring">
        <span className="trial-celebration-check">✓</span>
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`trial-sparkle trial-sparkle-${i}`} />
        ))}
      </div>
    </div>
  );
};

const IncorrectFlash = ({ visible }) => (visible ? <div className="trial-incorrect-flash" /> : null);

// ── One tappable card — used for both the sample and the choices ────────────

function MatchCard({ item, size = 'choice', state, onClick }) {
  const { tc } = useTranslation();
  const clickable = !!onClick;

  return (
    <div
      className={[
        'match-card',
        `match-card--${size}`,
        state ? `match-card--${state}` : '',
        clickable ? 'match-card--clickable' : '',
      ].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      <div className="match-card-img-wrap">
        {item.imageUrl
          ? <img src={item.imageUrl} alt={tc(item.label)} className="match-card-img" draggable={false} />
          : (
            <div className="match-card-word-bg">
              <span className="match-card-word">{tc(item.label)}</span>
            </div>
          )
        }
        {state === 'correct' && <div className="match-card-badge match-card-badge--correct">✓</div>}
        {state === 'wrong'   && <div className="match-card-badge match-card-badge--wrong">✗</div>}
      </div>
      {item.imageUrl && item.label && (
        <div className="match-card-label">{tc(item.label)}</div>
      )}
    </div>
  );
}

// ── Sound sample card — the prompt for MATCH_TYPES.SOUND trials. Attempts to
// play automatically when a round loads; the button also lets the child tap
// to replay as many times as they need before choosing a picture.

function SoundSampleCard({ audioUrl }) {
  const audioRef = useRef();
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {}); // autoplay may be blocked; tap replays it
  }, [audioUrl]);

  const replay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  };

  return (
    <div
      className={`match-card match-card--sample match-card--clickable match-sound-card${playing ? ' match-sound-card--playing' : ''}`}
      onClick={replay}
    >
      <div className="match-sound-icon-wrap">
        <span className="match-sound-icon">🔊</span>
      </div>
      <div className="match-card-label">Tap to hear again</div>
      <audio
        ref={audioRef}
        src={audioUrl}
        onPlay={() => setPlaying(true)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
}

// ── Variations modal ─────────────────────────────────────────────────────────
// Mirrors the deck session's VariationsModal (SessionPage.js): a grid of a
// card's known photos, tap to enable/disable which ones are eligible to be
// used this activity. Enabling just one photo makes that card pure identical
// matching; enabling several lets a round pair two different photos of the
// same item (generalization matching). Selections persist per user so they
// carry over next time this activity is played.

function VariationsModal({ trial, deckId, allMedia, initialActive, onDone, onClose }) {
  const { currentUser } = useAuth();
  const { tc } = useTranslation();
  const uid = currentUser?.uid;

  const [activeSet, setActiveSet] = useState(() => new Set(initialActive?.length ? initialActive : allMedia));
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const toggle = (url) => {
    setActiveSet(prev => {
      const next = new Set(prev);
      if (next.has(url)) {
        if (next.size <= 1) return prev; // keep at least one enabled
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const persist = async () => {
    if (!uid || saving) return;
    setSaving(true);
    await saveMatchingVariationSelections(uid, deckId, trial.id, [...activeSet]);
    setSaving(false);
    setSavedOk(true);
    setTimeout(() => setSavedOk(false), 2000);
  };

  const handleDone = async () => {
    await persist();
    onDone([...activeSet]);
    onClose();
  };

  return (
    <div className="variations-overlay" onClick={onClose}>
      <div className="var-modal-redesign" onClick={e => e.stopPropagation()}>

        <div className="var-header">
          <div className="var-header-left">
            <span className="var-header-emoji">🖼</span>
            <div>
              <h3 className="var-header-title">{tc(trial.prompt.label)}</h3>
              <p className="var-header-sub">Photos</p>
            </div>
          </div>
          <button className="var-close-btn" onClick={onClose} type="button">✕</button>
        </div>

        <hr className="var-divider" />

        <p className="var-hint">
          Tap a photo to enable or disable it. Enable just one for identical matching,
          or several so the activity can pair two different photos of the same item.
        </p>

        <div className="var-grid">
          {allMedia.map((url, i) => {
            const isActive = activeSet.has(url);
            return (
              <div
                key={url + i}
                className={`var-card${isActive ? ' var-card-on' : ' var-card-off'}`}
                onClick={() => toggle(url)}
              >
                <div className="var-card-img-wrap">
                  <img src={url} alt={trial.prompt.label} className="var-card-img" />
                  {isActive && <div className="var-card-check">✓</div>}
                  {!isActive && <div className="var-card-dim" />}
                </div>
              </div>
            );
          })}
        </div>

        <hr className="var-divider" style={{ marginTop: 12 }} />
        <div className="var-footer">
          <button className="var-save-btn" onClick={persist} disabled={saving} type="button">
            {savedOk ? '✓ Saved' : saving ? 'Saving…' : 'Save'}
          </button>
          <button className="var-done-btn" onClick={handleDone} disabled={saving} type="button">
            ✓ Done
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const MatchingPage = ({ generated = false, userSource = false }) => {
  const { deckId } = useParams();
  const navigate    = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const fromFirestore = generated || userSource;

  const staticDeck = fromFirestore ? null : MATCHING_DECKS.find(d => d.id === deckId);
  const [deck, setDeck]         = useState(staticDeck);
  const [deckLoading, setDeckLoading] = useState(fromFirestore);

  useEffect(() => {
    if (!fromFirestore) return;
    const collectionName = userSource ? 'userMatchingDecks' : 'generatedMatchingDecks';
    getDoc(doc(db, collectionName, deckId)).then(snap => {
      if (snap.exists()) setDeck({ id: snap.id, ...snap.data() });
      setDeckLoading(false);
    });
  }, [deckId, fromFirestore, userSource]);

  const trials = useMemo(
    () => (deck?.trials || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [deck]
  );

  // ── Setup state ──────────────────────────────────────────────────────────
  const [phase, setPhase]           = useState('setup'); // 'setup' | 'play' | 'complete'
  const [selectedIds, setSelectedIds] = useState(null);  // null until trials load
  const [search, setSearch]         = useState('');
  const [enabledMedia, setEnabledMedia] = useState({});  // trialId -> Set(url)
  const [variationsTrial, setVariationsTrial] = useState(null);
  const [trialCount, setTrialCount] = useState(5);

  // Reset everything when a different activity loads
  useEffect(() => {
    setPhase('setup');
    setSelectedIds(null);
    setEnabledMedia({});
    setSearch('');
  }, [deck?.id]);

  useEffect(() => {
    if (trials.length && selectedIds === null) {
      setSelectedIds(new Set(trials.map(tr => tr.id)));
      setTrialCount(Math.min(5, trials.length) || 1);
    }
  }, [trials, selectedIds]);

  const selectedTrials = trials.filter(tr => selectedIds?.has(tr.id));

  const toggleTrial = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(trials.map(tr => tr.id)));
  const clearAll  = () => setSelectedIds(new Set());

  // Load this trial's saved photo selections (if any) the first time its
  // Variations modal is opened, so preferences carry over between visits.
  const openVariations = (trial) => {
    setVariationsTrial(trial);
    if (!currentUser?.uid || enabledMedia[trial.id]) return;
    getMatchingVariationSelections(currentUser.uid, deck.id, trial.id).then(res => {
      if (res.success && res.data?.activeMedia?.length) {
        setEnabledMedia(prev => ({ ...prev, [trial.id]: new Set(res.data.activeMedia) }));
      }
    });
  };

  // ── Play state ───────────────────────────────────────────────────────────
  const [round,          setRound]          = useState(null);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [roundIndex,     setRoundIndex]     = useState(0);
  const [correctCount,   setCorrectCount]   = useState(0);
  const [answeredCount,  setAnsweredCount]  = useState(0);
  const [celebrating,    setCelebrating]    = useState(false);
  const [flashWrong,     setFlashWrong]     = useState(false);
  const timersRef = useRef([]);
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const isUnlimited = trialCount === 'unlimited';

  const startActivity = () => {
    if (selectedTrials.length === 0) return;
    setRoundIndex(0);
    setCorrectCount(0);
    setAnsweredCount(0);
    setSelectedChoice(null);
    setRound(buildRound(selectedTrials, enabledMedia, deck.matchType));
    setPhase('play');
  };

  const handleChoiceTap = (choice) => {
    if (selectedChoice !== null) return;
    setSelectedChoice(choice.id);

    const isCorrect = !!choice.isCorrect;
    setAnsweredCount(c => c + 1);
    if (isCorrect) {
      setCorrectCount(c => c + 1);
      setCelebrating(true);
      timersRef.current.push(setTimeout(() => setCelebrating(false), 900));
    } else {
      setFlashWrong(true);
      timersRef.current.push(setTimeout(() => setFlashWrong(false), 600));
    }

    timersRef.current.push(setTimeout(() => {
      const doneCount = roundIndex + 1;
      if (!isUnlimited && doneCount >= trialCount) {
        setPhase('complete');
      } else {
        setRound(buildRound(selectedTrials, enabledMedia, deck.matchType));
        setRoundIndex(i => i + 1);
        setSelectedChoice(null);
      }
    }, 1200));
  };

  const cardState = (choice) => {
    if (selectedChoice === null) return null;
    if (choice.isCorrect) return 'correct';
    if (choice.id === selectedChoice) return 'wrong';
    return null;
  };

  // ── Not found / loading ──────────────────────────────────────────────────

  if (deckLoading) {
    return (
      <div className="match-page match-page--center">
        <p style={{ color: 'var(--text-muted)' }}>{t('match.loading')}</p>
      </div>
    );
  }

  if (!deck || trials.length === 0) {
    return (
      <div className="match-page match-page--center">
        <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t('match.notFound')}</p>
        <button className="match-back-btn" onClick={() => navigate('/matching')}>← {t('match.back')}</button>
      </div>
    );
  }

  // ── Setup screen ───────────────────────────────────────────────────────────

  if (phase === 'setup') {
    const filtered = trials.filter(tr => (tr.prompt.label || '').toLowerCase().includes(search.toLowerCase()));

    return (
      <div className="match-page">
        <div className="match-header">
          <button className="match-back-btn" onClick={() => navigate('/matching')}>← {t('match.back')}</button>
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '4px 0 2px' }}>{deck.title}</h1>
        <p className="wz-field-hint" style={{ marginBottom: 18 }}>Choose which cards to practice, then pick how many trials.</p>

        <div className="wz-field" style={{ width: '100%', maxWidth: 640 }}>
          <div className="wz-field-row">
            <label className="wz-field-label">Select Target Cards</label>
            <span className="wz-count-badge">{selectedTrials.length} / {trials.length}</span>
          </div>
          <input
            className="wz-search"
            type="text"
            placeholder="Search cards…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, margin: '10px 0' }}>
            <button type="button" onClick={selectAll} className="match-back-btn" style={{ background: '#F3F4F6' }}>Select All</button>
            <button type="button" onClick={clearAll} className="match-back-btn" style={{ background: '#F3F4F6' }}>Clear All</button>
          </div>

          <div className="wz-card-grid">
            {filtered.map(tr => {
              const isSelected = selectedIds?.has(tr.id);
              const allMedia = mediaFor(tr);
              return (
                <div
                  key={tr.id}
                  className={`wz-card-item${isSelected ? ' wz-selected' : ''}`}
                  onClick={() => toggleTrial(tr.id)}
                >
                  <div className="wz-card-face" style={{ backgroundColor: '#EEF2FF' }}>
                    {tr.prompt.imageUrl ? (
                      <img src={tr.prompt.imageUrl} alt={tr.prompt.label} className="wz-card-img" />
                    ) : tr.prompt.audioUrl ? (
                      <span style={{ fontSize: 32 }}>🔊</span>
                    ) : null}
                  </div>
                  <span className="wz-card-label">{tr.prompt.label}</span>
                  <div className={`wz-checkbox${isSelected ? ' wz-checked' : ''}`}>
                    {isSelected && <span className="wz-checkmark">✓</span>}
                  </div>
                  {allMedia.length > 1 && (
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); openVariations(tr); }}
                      style={{
                        marginTop: 4, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', cursor: 'pointer',
                      }}
                    >
                      🖼 {allMedia.length} photos
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="wz-field" style={{ maxWidth: 280 }}>
          <label className="wz-field-label">Number of Trials</label>
          <select
            className="trials-select"
            value={trialCount}
            onChange={e => { const v = e.target.value; setTrialCount(v === 'unlimited' ? 'unlimited' : Number(v)); }}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n} trial{n > 1 ? 's' : ''}</option>)}
            <option value="unlimited">Unlimited</option>
          </select>
        </div>

        <button
          className="match-next-btn"
          style={{ marginTop: 8, opacity: selectedTrials.length === 0 ? 0.5 : 1 }}
          disabled={selectedTrials.length === 0}
          onClick={startActivity}
        >
          Start Activity →
        </button>

        {variationsTrial && (
          <VariationsModal
            trial={variationsTrial}
            deckId={deck.id}
            allMedia={mediaFor(variationsTrial)}
            initialActive={enabledMedia[variationsTrial.id] ? [...enabledMedia[variationsTrial.id]] : null}
            onDone={activeList => setEnabledMedia(prev => ({ ...prev, [variationsTrial.id]: new Set(activeList) }))}
            onClose={() => setVariationsTrial(null)}
          />
        )}
      </div>
    );
  }

  // ── Complete screen ───────────────────────────────────────────────────────

  if (phase === 'complete') {
    return (
      <div className="match-page match-page--center">
        <div className={celebrating ? 'match-celebrate' : ''}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
          <h2 style={{ margin: '0 0 8px' }}>{t('match.wellDone')}</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
            {correctCount} / {answeredCount} {t('match.correct')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="match-back-btn" onClick={startActivity}>{t('match.playAgain')}</button>
          <button className="match-back-btn" onClick={() => setPhase('setup')}>Choose Different Cards</button>
          <button className="match-back-btn match-back-btn--primary" onClick={() => navigate('/matching')}>
            {t('match.backToLibrary')}
          </button>
        </div>
      </div>
    );
  }

  // ── Play screen ────────────────────────────────────────────────────────────

  return (
    <div className="match-page">
      {celebrating && <CelebrationOverlay visible />}
      {flashWrong && <IncorrectFlash visible />}

      <div className="match-header">
        <button className="match-back-btn" onClick={() => setPhase('setup')}>← {t('match.back')}</button>
        <div className="match-progress">
          {isUnlimited ? `Trial ${roundIndex + 1}` : `${roundIndex + 1} / ${trialCount}`}
        </div>
      </div>

      <p className="match-instruction">
        {deck.matchType === MATCH_TYPES.SOUND ? t('match.instructionSound') : t('match.instruction')}
      </p>

      <div className="match-sample-wrap">
        {deck.matchType === MATCH_TYPES.SOUND
          ? <SoundSampleCard key={roundIndex} audioUrl={round.prompt.audioUrl} />
          : <MatchCard item={round.prompt} size="sample" />
        }
      </div>

      <div className={`match-choice-grid match-choice-grid--${round.choices.length}`}>
        {round.choices.map(choice => (
          <MatchCard
            key={choice.id}
            item={choice}
            size="choice"
            state={cardState(choice)}
            onClick={() => handleChoiceTap(choice)}
          />
        ))}
      </div>

      {isUnlimited && (
        <button
          className="match-back-btn"
          style={{ marginTop: 20 }}
          onClick={() => setPhase('complete')}
          disabled={answeredCount === 0}
        >
          End Activity
        </button>
      )}
    </div>
  );
};
